import { FilterGroups } from '@components/GalleryComponents/GalleryPageContent/GalleryPageContentFiltering';
import { useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { ProcessFilterGroups } from '@components/GalleryComponents/GalleryPageContent/GalleryPageContent';

const prioritizeJoyPrintedThemes = (cards: MarketingData, prioritizationSet: Set<string>): MarketingData => {
  const { joyPrintedThemes, otherThemes } = cards.reduce(
    (acc, curr) => {
      if (prioritizationSet.has(curr.variantThemeId)) {
        acc.joyPrintedThemes.push({ ...curr, isJoyPrintable: true });
      } else {
        acc.otherThemes.push(curr);
      }
      return acc;
    },
    { joyPrintedThemes: [], otherThemes: [] } as {
      joyPrintedThemes: MarketingData;
      otherThemes: MarketingData;
    }
  );

  return joyPrintedThemes.concat(otherThemes);
};

export const createFilterGroupsProcessor = (productType: GalleryProductType, data: GalleryECardsThemesVariantData): ProcessFilterGroups => {
  const joyPrintedThemeIdsSet = new Set(data.stationeryTemplatesContainer.pagedResults.edges.map((template) => template.node.themeId));
  return (filterGroups, { hasPrints }): FilterGroups => {
    if ((productType !== 'Invitation' && productType !== 'Save the Date') || !hasPrints) {
      return filterGroups;
    }
    /**
     * When viewing save the date or invitation cards, prioritize joy printed themes
     */
    return {
      match: prioritizeJoyPrintedThemes(filterGroups.match, joyPrintedThemeIdsSet),
      noMatch: prioritizeJoyPrintedThemes(filterGroups.noMatch, joyPrintedThemeIdsSet),
    };
  };
};

const getLowestPrintCardPrice = (productIds: ReadonlyArray<string>, products: ReadonlyArray<BlissStationeryProduct>): number | null => {
  const set = new Set<string>(productIds);
  const availablePrintProducts = products.filter((p) => set.has(p.id) && p.productType === 'stationeryCard');

  let lowestPrice: number | null = null;

  availablePrintProducts.forEach((product) => {
    let currentPrice: number | null = null;

    if (product.pricing.__typename === 'Bliss_StationeryTieredPricing') {
      currentPrice = Math.min(...product.pricing.priceTiers.map((tier) => tier.unitPriceInMinorUnits));
    }

    if (product.pricing.__typename === 'Bliss_StationeryTieredFlatFeePricing') {
      currentPrice = Math.min(...product.pricing.priceTiers.map((tier) => tier.totalPriceInMinorUnits));
    }

    if (product.pricing.__typename === 'Bliss_StationeryFlatFeePricing') {
      currentPrice = product.pricing.priceInMinorUnits;
    }

    if (currentPrice !== null && (lowestPrice === null || currentPrice < lowestPrice)) {
      lowestPrice = currentPrice;
    }
  });

  return lowestPrice;
};

export const useProcessPrintCards = (
  data: BlissGalleryECardsThemesData,
  variationType: 'savethedate' | 'invite',
  extraData?: MarketingData
) => {
  const [showJoyDesignsWithMatchingPrints, setShowDesignsWithMatchingPrints] = useState(false);

  const cards: MarketingData = useMemo(() => {
    const joyPrintedTemplates = new Map(
      data.bliss.stationeryTemplatesContainer.pagedResults.edges.map((template) => [template.node.themeId, template.node])
    );

    return data.bliss.themeVariants
      .filter((v) => v.theme)
      .concat(extraData || [])
      .map((card) => {
        const joyPrintTemplate = joyPrintedTemplates.get(card.theme.themeId);

        // Must clone so that we don't mutate the original reference
        const clonedCard: GalleryECard = cloneDeep(card);

        // If we're showing joy designs with matching prints + there's a matching StationryTemplate,
        // We want to update/create print marketing data
        if (joyPrintTemplate && joyPrintTemplate.approved && joyPrintTemplate.enabled) {
          clonedCard.isJoyPrintable = true;

          // Adding a query param because `GalleryECard` components appends on a `width` query param
          const thumbnailUrl = joyPrintTemplate.thumbnailUrl + '?v=1';
          clonedCard.imageUrl = thumbnailUrl;

          const maybePrintMarketingData = clonedCard.theme.marketingData.find((md) => md.variationType === 'prints');
          // Find a fallback marketing data variant to use as a reference
          // (data is mostly the same asides from the ones modified below)
          const maybeTargetVariationTypeMarketingData = clonedCard.theme.marketingData.find((md) => md.variationType === variationType);

          // If there is already a prints marketing data, update the imageUrl and vendor
          if (maybePrintMarketingData) {
            maybePrintMarketingData.imageUrl = thumbnailUrl;
            maybePrintMarketingData.vendor = 'Joy';
          }
          // If there is no prints marketing data, create one using the corresponding variationType marketing data
          else if (maybeTargetVariationTypeMarketingData) {
            clonedCard.theme.marketingData.push({
              ...maybeTargetVariationTypeMarketingData,
              variationType: 'prints',
              imageUrl: thumbnailUrl,
              vendor: 'Joy',
            });
          }

          const lowestPrintCardPrice = getLowestPrintCardPrice(joyPrintTemplate.productIds, data.bliss.stationeryProducts || []);

          if (Number.isFinite(lowestPrintCardPrice)) {
            clonedCard.stationeryPrintCardLowestPriceInMinorUnits = lowestPrintCardPrice as number;
          }
        }

        return clonedCard;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showJoyDesignsWithMatchingPrints, data, variationType, extraData]);

  return {
    setShowDesignsWithMatchingPrints,
    cards,
  };
};

const _createThemeLookupMap = (themeIds: string[]) => {
  return themeIds.reduce((acc, curr, idx) => {
    acc[curr] = {
      themeId: curr,
      position: themeIds.length - idx,
    };
    return acc;
  }, {} as { [key: string]: { themeId: string; position: number } });
};

const PRIORITIZED_INVITE_THEMES = _createThemeLookupMap([
  'bohemian_eucalyptus',
  'emrald_city',
  'la_corniche',
  'ivory_garden',
  'rustic_chic_5',
  'coastal',
  'interlocked_gold',
  'vineyard_vignette',
  'wreath',
  'dream_wash',
  'tropical_botanical',
  'chrysanthemum',
  'lavender_leaves',
  'moody_marble',
  'gold_foil_frame',
  'cerulean_blush',
]);

const PRIORITIZED_SAVE_THE_DATE_THEMES = _createThemeLookupMap([
  'love_and_leaves',
  'vineyard_vignette',
  'lavender_leaves',
  'bohemian_eucalyptus',
  'emrald_city',
  'rustic_chic_5',
  'minimalist_arch',
  'la_corniche',
  'lovely_doves',
  'northwest',
  'painted_garland',
  'ivory_garden',
  'boho_arch_ii',
  'formal_deco',
  'coastal',
  'classical_peonies',
]);

export const sortPrintGalleryCards = (productType: 'Save the Date' | 'Invitation', cards: GalleryECard[]) => {
  const prioritizationSet = productType === 'Save the Date' ? PRIORITIZED_SAVE_THE_DATE_THEMES : PRIORITIZED_INVITE_THEMES;
  return cards.sort((a, b) => {
    const aPosition = prioritizationSet[a.variantThemeId]?.position ?? -1;
    const bPosition = prioritizationSet[b.variantThemeId]?.position ?? -1;
    return bPosition - aPosition;
  });
};
